import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Collapse, Card, Spinner } from "react-bootstrap";
import { api } from "../../services/api.js";
import {
  FaChevronDown,
  FaChevronUp,
  FaUndoAlt,
  FaCalendarAlt,
} from "react-icons/fa";

import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, LabelList, Cell
} from "recharts";

import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer/index.jsx";

// ================= FORMATADORES =================
const formatMoeda = (v) => v?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const formatPercent = (v) => isNaN(v) || v === null ? "0,00%" : `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const formatLoja = (n) => String(n).padStart(2, '0');

const formatarMesExibicao = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return mesAno;
  return `${mesAno.slice(2)}/20${mesAno.slice(0, 2)}`;
};

const formatarParaInput = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return "";
  return `20${mesAno.slice(0, 2)}-${mesAno.slice(2)}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-2 border rounded shadow-sm" style={{ fontSize: '12px', zIndex: 3000 }}>
        <p className="fw-bold mb-1 border-bottom">{label}</p>
        <div className="text-primary">Venda: {formatMoeda(data.venda)}</div>
        <div className="text-secondary">Meta: {formatMoeda(data.meta)}</div>
        <div className="fw-bold">Atingido: {formatPercent(data.atingido)}</div>
        <div className="text-info">Margem: {formatPercent(data.margem)}</div>
      </div>
    );
  }
  return null;
};

const VendaDeptoSecao = () => {
  const [mes, setMes] = useState("");
  const [dadosDepto, setDadosDepto] = useState([]);
  const [dadosSecao, setDadosSecao] = useState({});
  const [dadosLoja, setDadosLoja] = useState({});
  const [dadosGrafico, setDadosGrafico] = useState([]);
  const [tituloGrafico, setTituloGrafico] = useState("Departamentos");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    const hoje = new Date();
    const aa = String(hoje.getFullYear()).slice(2);
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const mesPadrao = `${aa}${mm}`;
    setMes(mesPadrao);
    carregarDepto(mesPadrao);
  }, []);

  const carregarDepto = async (mesConsulta) => {
    setLoading(true);
    setDadosSecao({});
    setDadosLoja({});
    
    try {
      const { data } = await api.get(`/venda/mes/depto?mes=${mesConsulta || mes}&usuario=${localStorage.getItem("chapa")}`);
      setDadosDepto(data || []);
      setDadosGrafico(data.map(d => ({ 
        nome: d.ddepartamento.split("-")[1] || d.ddepartamento, 
        atingido: d.atingido, venda: d.venda, meta: d.meta, margem: d.margem 
      })));
      setTituloGrafico("Departamentos");
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const buscarSecao = async (depCod, depNome) => {
    if (dadosSecao[depCod]) {
      const copy = { ...dadosSecao }; delete copy[depCod];
      setDadosSecao(copy);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/venda/mes/deptosecao?mes=${mes}&usuario=${localStorage.getItem("chapa")}&depto=${depCod}`);
      setDadosSecao({ ...dadosSecao, [depCod]: data });
      setDadosGrafico(data.map(s => ({ nome: s.descsecao, atingido: s.atingido, venda: s.venda, meta: s.meta, margem: s.margem })));
      setTituloGrafico(`Seções: ${depNome}`);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const buscarLoja = async (depCod, secCod, secNome) => {
    const key = `${depCod}-${secCod}`;
    if (dadosLoja[key]) {
      const copy = { ...dadosLoja }; delete copy[key];
      setDadosLoja(copy);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/venda/mes/deptosecaoloja?mes=${mes}&usuario=${localStorage.getItem("chapa")}&depto=${depCod}&secao=${secCod}`);
      setDadosLoja({ ...dadosLoja, [key]: data });
      setDadosGrafico(data.map(l => ({ nome: `${formatLoja(l.filial)}`, atingido: l.atingido, venda: l.venda, meta: l.meta, margem: l.margem })));
      setTituloGrafico(`Lojas: ${secNome}`);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Cálculo do Total Geral dos Departamentos
  const resumoGeral = dadosDepto.reduce((acc, curr) => {
    acc.venda += curr.venda;
    acc.meta += curr.meta;
    acc.somaMargemVenda += (curr.margem * curr.venda); // Ponderação simples para margem
    return acc;
  }, { venda: 0, meta: 0, somaMargemVenda: 0 });

  const atingidoGeral = resumoGeral.meta > 0 ? (resumoGeral.venda / resumoGeral.meta) * 100 : 0;
  const margemGeral = resumoGeral.venda > 0 ? (resumoGeral.somaMargemVenda / resumoGeral.venda) : 0;

  return (
    <div className="arbor-page">
      <Menu modulo="ARBOR" />
      
      {loading && (
        <div className="loading-overlay">
          <Spinner animation="border" variant="primary" />
          <span className="ms-2 fw-bold text-primary">Atualizando...</span>
        </div>
      )}

      <button className="btn-floating-calendar" onClick={() => setShowModal(true)}>
        <FaCalendarAlt />
      </button>

      <div className="container-fluid content-wrapper p-3">
        <div className="d-flex justify-content-between align-items-center mb-3 mt-1">
          <h5 className="fw-bold m-0 text-muted">
            Período: <span className="text-primary">{formatarMesExibicao(mes)}</span>
          </h5>
          <Button size="sm" variant="outline-primary" onClick={() => carregarDepto()} disabled={loading}>
            <FaUndoAlt /> Reset
          </Button>
        </div>

        {/* GRÁFICO */}
        <div className="bg-white border rounded shadow-sm mb-4 p-2">
          <h6 className="text-center small text-muted mb-3">{tituloGrafico} (% Atingido)</h6>
          <div style={{ width: "100%", height: 250 }}>
            <ResponsiveContainer>
              <BarChart data={dadosGrafico}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="nome" tick={{fontSize: 9}} />
                <YAxis tick={{fontSize: 10}} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="atingido" radius={[4, 4, 0, 0]}>
                  {dadosGrafico.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.atingido >= 100 ? "#28a745" : "#007bff"} />
                  ))}
                  <LabelList dataKey="atingido" position="top" formatter={(v) => `${v.toFixed(1)}%`} style={{fontSize: 10, fontWeight: 'bold'}} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* TOTALIZADOR DESTAQUE (SÓ MOBILE) */}
        {isMobile && dadosDepto.length > 0 && (
          <Card className="mb-3 border-0 shadow-sm bg-dark text-white text-center py-2">
            <div className="small opacity-75">TOTAL GERAL</div>
            <div className="d-flex justify-content-around mt-1">
              <div><small>Venda:</small> <br/><b>{formatMoeda(resumoGeral.venda)}</b></div>
              <div><small>Ating.:</small> <br/><b className={atingidoGeral >= 100 ? "text-success" : "text-warning"}>{formatPercent(atingidoGeral)}</b></div>
              <div><small>Margem:</small> <br/><b className={margemGeral >= 25 ? "text-success" : "text-danger"}>{formatPercent(margemGeral)}</b></div>
            </div>
          </Card>
        )}

        {/* VISÃO MOBILE (Cards) */}
        {isMobile && (
          <div className="mobile-list pb-5">
            {dadosDepto.map((d) => (
              <Card key={d.dep} className="mb-3 border-0 shadow-sm">
                <Card.Header className="bg-white py-3 border-0" onClick={() => buscarSecao(d.dep, d.ddepartamento)}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-bold text-dark">{d.ddepartamento}</span>
                    {dadosSecao[d.dep] ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                  <div className="row g-1 text-center bg-light rounded py-2 border">
                    <div className="col-6 border-end border-bottom x-small">V: <b>{formatMoeda(d.venda)}</b></div>
                    <div className="col-6 border-bottom x-small">M: <b>{formatMoeda(d.meta)}</b></div>
                    <div className="col-6 border-end x-small">AT: <b className={d.atingido >= 100 ? "text-success" : "text-danger"}>{formatPercent(d.atingido)}</b></div>
                    <div className="col-6 x-small">MG: <b>{formatPercent(d.margem)}</b></div>
                  </div>
                </Card.Header>
                <Collapse in={!!dadosSecao[d.dep]}>
                  <Card.Body className="bg-light pt-0 px-2 pb-2">
                    {dadosSecao[d.dep]?.map((s) => (
                      <div key={s.codsecao} className="bg-white rounded p-2 mb-2 border shadow-xs">
                        <div className="d-flex justify-content-between align-items-center fw-bold mb-1 border-bottom pb-1" 
                             onClick={() => buscarLoja(d.dep, s.codsecao, s.descsecao)} style={{cursor: 'pointer'}}>
                          <span className="text-primary" style={{fontSize: '0.85rem'}}>{s.descsecao}</span>
                          <div className="d-flex align-items-center">
                            <span className="me-2 text-dark">{formatPercent(s.atingido)}</span>
                            {dadosLoja[`${d.dep}-${s.codsecao}`] ? <FaChevronUp size={12}/> : <FaChevronDown size={12}/>}
                          </div>
                        </div>
                        <div className="row g-0 text-center x-small text-muted mb-1 py-1 bg-light-blue rounded">
                           <div className="col-4 border-end">V: {formatMoeda(s.venda)}</div>
                           <div className="col-4 border-end">M: {formatMoeda(s.meta)}</div>
                           <div className="col-4">MG: {formatPercent(s.margem)}</div>
                        </div>
                        <Collapse in={!!dadosLoja[`${d.dep}-${s.codsecao}`]}>
                          <div className="mt-2 border-top pt-2">
                            {dadosLoja[`${d.dep}-${s.codsecao}`]?.map((l, i) => (
                              <div key={i} className="mb-2 p-2 border-start border-primary border-3 bg-white shadow-xs rounded">
                                <div className="d-flex justify-content-between fw-bold mb-1" style={{fontSize: '0.8rem'}}>
                                  <span>LOJA {formatLoja(l.filial)}</span>
                                  <span className={l.atingido >= 100 ? "text-success" : "text-danger"}>{formatPercent(l.atingido)}</span>
                                </div>
                                <div className="row g-1 x-small text-muted mt-1">
                                  <div className="col-6">Venda: <b>{formatMoeda(l.venda)}</b></div>
                                  <div className="col-6 text-end">Meta: <b>{formatMoeda(l.meta)}</b></div>
                                  <div className="col-12 border-top pt-1">Margem: <b>{formatPercent(l.margem)}</b></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </Collapse>
                      </div>
                    ))}
                  </Card.Body>
                </Collapse>
              </Card>
            ))}
          </div>
        )}

        {/* VISÃO DESKTOP (Tabela) */}
        {!isMobile && (
          <div className="table-responsive shadow-sm rounded">
            <Table hover size="sm" className="bg-white table-align-middle mb-0">
              <thead className="table-dark">
                <tr>
                  <th width="40"></th>
                  <th>Descrição</th>
                  <th className="text-end">Venda</th>
                  <th className="text-end">Meta</th>
                  <th className="text-end">% Ating.</th>
                  <th className="text-end">Margem</th>
                </tr>
              </thead>
              <tbody>
                {/* LINHA DE TOTAL NO TOPO DA TABELA */}
                <tr className="table-secondary fw-bold border-bottom-2">
                  <td></td>
                  <td className="text-uppercase text-primary">TOTAL GERAL</td>
                  <td className="text-end text-primary">{formatMoeda(resumoGeral.venda)}</td>
                  <td className="text-end">{formatMoeda(resumoGeral.meta)}</td>
                  <td className={`text-end ${atingidoGeral >= 100 ? "text-success" : "text-danger"}`}>
                    {formatPercent(atingidoGeral)}
                  </td>
                  <td className="text-end">{formatPercent(margemGeral)}</td>
                </tr>

                {dadosDepto.map((d) => (
                  <React.Fragment key={d.dep}>
                    <tr onClick={() => buscarSecao(d.dep, d.ddepartamento)} style={{ cursor: "pointer" }} className="fw-bold border-bottom">
                      <td className="text-center">{dadosSecao[d.dep] ? <FaChevronUp /> : <FaChevronDown />}</td>
                      <td>{d.ddepartamento}</td>
                      <td className="text-end">{formatMoeda(d.venda)}</td>
                      <td className="text-end">{formatMoeda(d.meta)}</td>
                      <td className={`text-end ${d.atingido >= 100 ? "text-success" : "text-danger"}`}>{formatPercent(d.atingido)}</td>
                      <td className="text-end">{formatPercent(d.margem)}</td>
                    </tr>
                    {dadosSecao[d.dep]?.map((s) => (
                      <React.Fragment key={s.codsecao}>
                        <tr className="bg-light" onClick={() => buscarLoja(d.dep, s.codsecao, s.descsecao)} style={{ cursor: "pointer" }}>
                          <td className="text-center">{dadosLoja[`${d.dep}-${s.codsecao}`] ? <FaChevronUp size={12}/> : <FaChevronDown size={12}/>}</td>
                          <td style={{ paddingLeft: "30px" }}>{s.descsecao}</td>
                          <td className="text-end">{formatMoeda(s.venda)}</td>
                          <td className="text-end">{formatMoeda(s.meta)}</td>
                          <td className="text-end">{formatPercent(s.atingido)}</td>
                          <td className="text-end">{formatPercent(s.margem)}</td>
                        </tr>
                        {dadosLoja[`${d.dep}-${s.codsecao}`]?.map((l, idx) => (
                          <tr key={idx} className="small text-muted">
                            <td></td>
                            <td style={{ paddingLeft: "60px" }}>Loja {formatLoja(l.filial)}</td>
                            <td className="text-end">{formatMoeda(l.venda)}</td>
                            <td className="text-end">{formatMoeda(l.meta)}</td>
                            <td className="text-end">{formatPercent(l.atingido)}</td>
                            <td className="text-end">{formatPercent(l.margem)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm" style={{zIndex: 3000}}>
        <Modal.Header closeButton><Modal.Title className="fs-6">Selecionar Período</Modal.Title></Modal.Header>
        <Modal.Body>
          <input 
            type="month" 
            className="form-control" 
            value={formatarParaInput(mes)}
            onChange={(e) => {
              if(!e.target.value) return;
              const [a, m] = e.target.value.split("-");
              const novo = `${a.slice(2)}${m}`;
              setMes(novo); 
              carregarDepto(novo); 
              setShowModal(false);
            }} 
          />
        </Modal.Body>
      </Modal>

      <Footer />

      <style>{`
        .arbor-page { background-color: #f8f9fa; min-height: 100vh; }
        .content-wrapper { padding-top: 85px !important; } 
        .loading-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(255,255,255,0.7); display: flex;
          justify-content: center; align-items: center; z-index: 9999;
        }
        .btn-floating-calendar {
          position: fixed; bottom: 85px; right: 20px;
          width: 55px; height: 55px; border-radius: 50%;
          background: #28a745; color: white; border: none;
          z-index: 2000; box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          display: flex; justify-content: center; align-items: center; font-size: 20px;
        }
        .bg-light-blue { background-color: #f0f8ff !important; }
        .x-small { font-size: 0.72rem; }
        .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .border-bottom-2 { border-bottom: 2px solid #dee2e6 !important; }
        @media (max-width: 768px) {
          .content-wrapper { padding-top: 75px !important; }
        }
      `}</style>
    </div>
  );
};

export default VendaDeptoSecao;